import {Datepicker} from "vanillajs-datepicker";

/**
 * Hook into Alpine so that it is available no matter at which point Alpine loads.
 */
document.addEventListener('alpine:init', () => {

    /**
     * Implementation Vanilla JS Datepicker.
     */
    Alpine.data('datepicker', (modelName, rules, hasFutureDates = false) => ({

        /**
         * @type {string} Wire model name.
         */
        modelName: modelName,

        /**
         * @type {array} Array of rules from MSA for validating dates. It can be one of the key from validationOptionsMap property.
         */
        rules: JSON.parse(rules),

        /**
         * @type {boolean} Determine if datepicker has futures dates.
         */
        hasFutureDates: hasFutureDates,

        datepicker: null,

        datePickerLevel: 0,

        monthPickerLevel: 1,

        yearPickerLevel: 2,

        noDay: false,

        noMonth: false,

        defaultFormat: '',

        dateFormat: 'mm/dd/yyyy',

        monthFormat: 'mm/yyyy',

        yearFormat: 'yyyy',

        shortMonthFormat: 'mm/yy',

        currentFormat: constants.current_date,

        validationOptionsMap: {
            'MONTH_DAY_YEAR': 'mm/dd/yyyy',
            'MONTH_YEAR': 'mm/yyyy',
            'YEAR_ONLY': 'yyyy',
            'CURRENT': constants.current_date,
            'SHORT_MONTH_YEAR': 'mm/yy'
        },

        current: false,

        preventNextBlur: false,

        init() {
            this.initDefaultFormat();
            this.initializeWatchers();
            this.initializeDatepicker();
        },

        initDefaultFormat() {
            this.defaultFormat = this.dateFormat;
            if (this.rules.length === 1) {
                const formatKey = this.rules;

                const formatValue = this.validationOptionsMap[formatKey] || null;
                this.defaultFormat = formatValue ?? this.dateFormat;
            }
        },

        initializeWatchers() {
            this.$watch('noMonth', (val) => {
                if (val && !this.noDay) {
                    this.noDay = true;
                }

                const options = {
                    pickLevel: this.matchPickLevel(this.dateFormat),
                    format: this.dateFormat,
                }

                if (val) {
                    options.pickLevel = this.matchPickLevel(this.yearFormat);
                    options.format = this.yearFormat;
                } else {
                    if (this.noDay) {
                        options.pickLevel = this.matchPickLevel(this.monthFormat);
                        options.format = this.monthFormat;
                    }
                }

                this.$refs.input.datepicker.setOptions(options);
            });

            this.$watch('noDay', (val) => {
                if (!val && this.noMonth) {
                    this.noMonth = false;
                }

                const options = {
                    pickLevel: this.matchPickLevel(this.dateFormat),
                    format: this.dateFormat,
                }

                if (val) {
                    if (this.noMonth) {
                        options.pickLevel = this.matchPickLevel(this.yearFormat);
                        options.format = this.yearFormat;
                    } else {
                        options.pickLevel = this.matchPickLevel(this.monthFormat);
                        options.format = this.monthFormat;
                    }
                }

                this.$refs.input.datepicker.setOptions(options);
            });

            this.$watch('current', (val) => {
                if (this.current) {
                    this.$refs.input.datepicker.setDate(null);
                    setTimeout(() => {
                        this.$refs.input.value = 'Current';
                    })
                }
            });
        },

        initializeDatepicker() {
                this.datepicker = new Datepicker(this.$refs.input,
                    {
                        autohide: true,
                        updateOnBlur: false,
                        format: {
                            toValue: (date, format, locale) => {
                                if (date.toLowerCase() === this.currentFormat.toLowerCase()) return null;

                                try {
                                    return Datepicker.parseDate(date, format, locale);
                                } catch (e) {
                                    return null;
                                }
                            },
                            toDisplay: (date, format, locale) => {
                                if (this.$refs.input.value.toLowerCase() === this.currentFormat.toLowerCase()
                                    && this.isAllowedFormat(this.currentFormat)
                                ) {
                                    return 'Current';
                                }
                                let formatValue = this.defaultFormat;

                                if (this.noDay === true && this.noMonth === true) {
                                    formatValue = this.yearFormat;
                                } else if (this.noDay === true && this.noMonth === false) {
                                    formatValue = this.yearFormat;
                                }

                                return Datepicker.formatDate(date, formatValue, locale);
                            },
                        },
                        startView: 0,
                        pickLevel: this.matchPickLevel(this.defaultFormat),
                        maxDate: !this.hasFutureDates ? new Date() : null,
                        enableOnReadonly: false,
                        prevArrow: '<i class="fas fa-chevron-left"></i>',
                        nextArrow: '<i class="fas fa-chevron-right"></i>',
                    });


            // Trick to prevent auto refresh input field when populated custom value (like: current)
            this.$refs.input.datepicker.refresh = () => {};

            this.$refs.input.addEventListener('input', (e) => {
                this.$refs.input.value = this.removeInvalidCharacters(this.$refs.input.value);

                if (e.target.value.trim().toLowerCase() === this.currentFormat.toLowerCase()) {
                    this.current = true;

                    e.stopImmediatePropagation();
                    return;
                }

                if (this.current) {
                    this.current = false;
                }

                const format = this.identifyDateFormat(e.target.value.trim());
                const allowedFormat = this.isAllowedFormat(format);
                let date = null

                if (format && allowedFormat) {
                    switch (format) {
                        case this.yearFormat:
                            this.noDay = true;
                            this.noMonth = true;
                            break;
                        case this.monthFormat:
                            this.noDay = true;
                            this.noMonth = false;
                            break;
                        case this.dateFormat:
                            this.noDay = false;
                            this.noMonth = false;
                            break;
                        default:
                            this.noDay = false;
                            this.noMonth = false;
                    }

                    date = Datepicker.parseDate(e.target.value.trim(), format);
                }

                if (date) {
                    this.$refs.input.datepicker.setDate(date);
                }
            });

            this.$refs.input.addEventListener('blur', (e) => {
                if (!this.preventNextBlur && this.$wire.get(this.modelName) !== this.$refs.input.value) {
                    this.$wire.set(this.modelName, this.$refs.input.value);
                }
                this.preventNextBlur = false;
            });
        },

        matchPickLevel(format) {
            switch (format) {
                case this.yearFormat:
                    return this.yearPickerLevel;
                case this.monthFormat:
                case this.shortMonthFormat:
                    return this.monthPickerLevel;
                case this.dateFormat:
                    return this.datePickerLevel;
                default:
                    return this.datePickerLevel;
            }
        },

        identifyDateFormat(dateString) {
            if (this.isYearFormat(dateString)) {
                return this.yearFormat;
            } else if (this.isMonthFormat(dateString)) {
                return this.monthFormat;
            } else if (this.isShortMonthFormat(dateString)) {
                return this.shortMonthFormat;
            } else if (this.isFullDateFormat(dateString)) {
                return this.dateFormat;
            } else if (this.isCurrentKeyword(dateString)) {
                return this.currentFormat;
            } else {
                return null;
            }
        },

        isAllowedFormat(format) {
            return Object.entries(this.validationOptionsMap).some(
                ([key, val]) => val === format && this.rules.includes(key)
            )
        },

        isYearFormat(val) {
            return /^\d{4}$/.test(val);
        },

        isMonthFormat(val) {
            return /^\d{2}\/\d{4}$/.test(val)
        },

        isShortMonthFormat(val) {
            return /^\d{2}\/\d{2}$/.test(val)
        },

        isFullDateFormat(val) {
            return /^\d{2}\/\d{2}\/\d{4}$/.test(val)
        },

        isCurrentKeyword(val) {
            return val.toLowerCase() === this.currentFormat.toLowerCase()
        },

        removeInvalidCharacters(val) {
            return val.replace(/[^0-9\/]/g, '');
        }
    }));
});